import io from 'socket.io-client';
import { getBackendUrl } from '../config';
import { readToken, refreshAccessToken } from './authSession';
import { startTracking, stopTracking, getIdleMs, onBecameActive } from './presenceTracker';

const HEARTBEAT_INTERVAL_MS = 30_000;

class SocketWorker {
  constructor(companyId, userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.heartbeatInterval = null;
      this.unsubscribeBecameActive = null;
      this.refreshingToken = false;
      this.pageLifecycleCleanup = null;
      this.configureSocket();
      this.bindPageLifecycle();
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    const backendBaseUrl = (
      getBackendUrl() ||
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    ).replace(/\/$/, '');

    this.socket = io(`${backendBaseUrl}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      // Função → o socket.io relê o token a cada (re)conexão, pegando um token
      // renovado sem precisar recriar a conexão.
      auth: (cb) => cb({ token: readToken() }),
    });

    this.socket.on('connect', () => {
      this.refreshingToken = false;
      this.startHeartbeat();
    });

    this.socket.on('connect_error', (err) => {
      // Token expirado no handshake: tenta 1 refresh e deixa o socket.io
      // reconectar (a função auth relê o token novo). Sem loop: só uma tentativa
      // até um connect bem-sucedido resetar a flag.
      if (err?.message === 'unauthorized' && !this.refreshingToken) {
        this.refreshingToken = true;
        this.tryRefreshToken();
      }
    });

    this.socket.on('disconnect', () => {
      // disconnected
      this.stopHeartbeat();
    });
  }

  /**
   * Ciclo de vida da página (bfcache). Ao entrar no back-forward cache o
   * navegador congela a aba e derruba o WebSocket, mas o socket.io não fica
   * sabendo: ele só percebe a queda quando a aba volta e aí dispara a política
   * de reconexão às cegas, abrindo uma sessão engine.io nova atrás da outra
   * ("WebSocket ... failed: Page entered Back-Forward Cache" repetido dezenas
   * de vezes no console). Não é só ruído — cada sessão custa um handshake
   * completo e uma carga do usuário (queues, role, permissions, whatsapps) no
   * backend. Então desligamos de propósito ao congelar e religamos ao
   * restaurar.
   */
  bindPageLifecycle() {
    if (typeof window === 'undefined' || this.pageLifecycleCleanup) return;

    // disconnect() do socket.io também cancela os timers de reconexão — é isso
    // que impede a rajada enquanto a aba está congelada.
    const suspend = () => this.socket?.disconnect();
    const resume = () => {
      if (this.socket && !this.socket.connected) this.socket.connect();
    };

    // `persisted` separa bfcache de saída definitiva da página: sem ele
    // religaríamos no load normal, onde o socket já está conectando sozinho.
    const onPageHide = (event) => {
      if (event.persisted) suspend();
    };
    const onPageShow = (event) => {
      if (event.persisted) resume();
    };

    // Só bfcache, nunca o `freeze` da Page Lifecycle API: dentro do onfreeze o
    // navegador só permite fetch keepalive, e o disconnect() sempre manda o
    // pacote de fechamento pela rede. Bloqueado, o engine antigo fica órfão
    // esperando `drain` e, quando a aba volta, dispara GET/POST com o sid morto
    // (400 "Session ID unknown"). Numa aba só congelada, o ping do próprio
    // socket.io detecta a sessão expirada e reconecta uma vez — sem rajada.
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    this.pageLifecycleCleanup = () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      this.pageLifecycleCleanup = null;
    };
  }

  async tryRefreshToken() {
    // Single-flight compartilhado com o interceptor do axios: se o HTTP já está
    // renovando, aproveitamos o mesmo POST em vez de disparar um concorrente.
    const token = await refreshAccessToken();
    if (!token) {
      // Sessão inválida; interrompe reconexões para não iterar em vão.
      this.socket?.close();
      return;
    }
    // Recusa do middleware (CONNECT_ERROR) não entra na política de reconexão:
    // o socket.io-client desiste do namespace e o socket fica inativo para
    // sempre — a página segue de pé, mas nenhum evento chega (mensagem presa em
    // "Enviando..." até o F5). Por isso o connect() é explícito; a função
    // `auth` relê o token novo do localStorage.
    if (this.socket && !this.socket.active) this.socket.connect();
  }

  /**
   * Heartbeat de presença: informa ao backend o tempo de inatividade a cada
   * 30s (decide online/away no servidor). Emite um batimento imediato no
   * connect — evita flapping em login/F5 — e outro na volta da ausência.
   */
  startHeartbeat() {
    this.stopHeartbeat();
    startTracking();
    const send = (idleMs) => {
      if (this.socket?.connected) {
        this.socket.emit('heartbeat', { idleMs: idleMs ?? getIdleMs() });
      }
    };
    send(0);
    this.heartbeatInterval = setInterval(() => send(), HEARTBEAT_INTERVAL_MS);
    this.unsubscribeBecameActive = onBecameActive(() => send(0));
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.unsubscribeBecameActive) {
      this.unsubscribeBecameActive();
      this.unsubscribeBecameActive = null;
    }
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter((cb) => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    this.stopHeartbeat();
    stopTracking();
    if (this.pageLifecycleCleanup) this.pageLifecycleCleanup();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      SocketWorker.instance = null;
      // disconnected manually
    }
  }

  get connected() {
    return Boolean(this.socket?.connected);
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
      this.bindPageLifecycle();
    }
  }

  forceReconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    }
  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
